
/* eslint-disable max-classes-per-file */

import { NgClass } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { ButtonComponent } from "@components/button/button.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import {
    assign,
    clone,
    isInteger,
    isNil,
    isNumber,
    orderBy,
    sortBy,
    sum
} from "lodash";
import {
    Observable,
    Subject,
    firstValueFrom,
    forkJoin,
    of
} from "rxjs";
import { filter, map, take, takeUntil } from "rxjs/operators";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SimpleSelectionItem } from "~app/common/components/selector/simple-selection-item";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ISelectValue } from "~app/common/models/selectValueModel";
import { Utils as utils } from "~app/common/services/utils";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ISearchHandelssortimentModel } from "~app/features/forsaljning/handelssortiment/handelssortiment.models";
import { ForkalkylsmallarDialogService } from "~app/features/virkeskop/forkalkylsmallar/components/forkalkylsmallar-dialog.service";
import { ForkalkylsmallHandelssortimentDTO } from "~app/features/virkeskop/forkalkylsmallar/models/ForkalkylsmallHandelssortimentDTO";
import { ForkalkylsmallQueryResponseDTO } from "~app/features/virkeskop/forkalkylsmallar/models/ForkalkylsmallQueryResponseDTO";
import { TilldelaHandelssortimentDTO } from "~app/features/virkeskop/forkalkylsmallar/models/TilldelaHandelssortimentDTO";
import { TilldelaHandelssortimentsGruppDTO } from "~app/features/virkeskop/forkalkylsmallar/models/TilldelaHandelssortimentsGruppDTO";
import { MinaHandelssortimentDialogService } from "~app/features/virkeskop/mina-handelssortiment/mina-handelssortiment-dialog.service";
import { Atgard } from "~app/features/virkeskop/models/atgard-models";
import { Kontrakt, VirkeskopKontraktstyp } from "~app/features/virkeskop/models/kontrakt-models";
import {
    HandelssortimentVirkeskostnadOption,
    IHandelssortimentVirkeskostnadOption,
    Virkeskostnad,
    Virkeskostnader
} from "~app/features/virkeskop/models/virkeskostnader/virkeskostnader-models";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { ForkalkylService } from "~app/features/virkeskop/services/forkalkyl.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { VirkeskostnaderMediatorService } from "~app/features/virkeskop/services/virkeskostnader-mediator.service";
import { VirkeskostnaderUtbyteService } from "~app/features/virkeskop/services/virkeskostnader-utbyte.service";
import { canEdit } from "~app/role-permissions";
import { ConfirmDialogResponse, Handelssortimentsgrupp, Mattslag, StatiskaProduktionsomradeIdn } from "../../../../../common/models/constants";
import { SkapaMallFranHandelssortimentComponent } from "../../../forkalkylsmallar/components/skapa-mall-fran-handelssortiment/skapa-mall-fran-handelssortiment.component";
import { ForkalkylmallarService } from "../../../services/forkalkylmallar.service";
import { BeraknatUtbyteButtonComponent } from "../beraknat-utbyte-button/beraknat-utbyte-button.component";
import { BeraknatUtbyteValidationComponent } from "../beraknat-utbyte-validation/beraknat-utbyte-validation.component";
import { VirkeskostnadCellNames, VirkeskostnadColumnDefinition, VirkeskostnadTableBinder } from "../utilities/virkeskostnad-table-binder";
export class AtgardVirkeskostnadCalculator {
    static calculateAverage(numbers: number[], col: VirkeskostnadColumnDefinition, tableData: Virkeskostnad[]): number {
        if (numbers.length > 0) {
            let summaKolumn: number;
            if (col.cellName === "grundpris") {
                // summera grundpriset * antal, grundpris är satt mot valt måttslag
                summaKolumn = sum(tableData.map(d => d.grundpris * d.kvantitet));
            } else {
                summaKolumn = sum(tableData.map(d => d[col.cellName] * d.kvantitetM3fub));
            }
            const summeradM3Fub = sum(tableData.map(d => d.kvantitetM3fub));
            return (summaKolumn / summeradM3Fub);
        }
        return 0;
    }
}

export const validateAsSignedInteger = (value: any) => {
    if (value === "") {
        return true;
    }

    // parseFloat istället för parseInt för att kunna hantera fall där användaren matar in t.ex. 2,0
    const num = parseFloat(value);
    return isNumber(num) && isInteger(num);
};

export const virkeskostnadColumns: VirkeskostnadColumnDefinition[] = [
    {
        header: "Måttsl",
        cellName: VirkeskostnadCellNames.mattslag,
        readOnly: false,
        width: 100,
        type: "dropdown",
        source: [],
        sumText: Mattslag.M3F,
        validateFn: () => true
    },
    {
        header: "m3f",
        cellName: "kvantitetM3fub",
        readOnly: true,
        disabled: () => true,
        type: "numeric",
        roundToInteger: true,
        sumFunction: sum,
        tooltip: ""
    },
    {
        header: "Kvant",
        cellName: "kvantitet",
        readOnly: false,
        validateFn: validateAsSignedInteger,
        sumText: "--",
        type: "numeric",
        tooltip: ""
    },
    {
        header: "Grundpris",
        cellName: "grundpris",
        readOnly: false,
        validateFn: validateAsSignedInteger,
        type: "numeric",
        sumFunction: AtgardVirkeskostnadCalculator.calculateAverage,
        roundToInteger: true,
        tooltip: ""
    },
    {
        header: "TA",
        cellName: "tillaggAvdrag",
        readOnly: false,
        validateFn: validateAsSignedInteger,
        type: "numeric",
        sumFunction: AtgardVirkeskostnadCalculator.calculateAverage,
        roundToInteger: true,
        tooltip: ""
    },
    {
        header: "FSC",
        cellName: "fsc",
        readOnly: false,
        validateFn: validateAsSignedInteger,
        type: "numeric",
        sumFunction: AtgardVirkeskostnadCalculator.calculateAverage,
        roundToInteger: true,
        tooltip: ""
    },
    {
        header: "PEFC",
        cellName: "pefcSC",
        readOnly: false,
        validateFn: validateAsSignedInteger,
        type: "numeric",
        sumFunction: AtgardVirkeskostnadCalculator.calculateAverage,
        roundToInteger: true,
        tooltip: ""
    },
    {
        header: "TB",
        cellName: "tackningsbidrag",
        readOnly: false,
        validateFn: validateAsSignedInteger,
        type: "numeric",
        sumFunction: AtgardVirkeskostnadCalculator.calculateAverage,
        roundToInteger: true,
        tooltip: ""
    }
];

@Component({
    selector: "rask-atgard-virkeskostnad",
    templateUrl: "./atgard-virkeskostnad.component.html",
    styleUrls: ["./atgard-virkeskostnad.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TreeSelectorComponent,
        NgClass,
        BeraknatUtbyteButtonComponent,
        TextmultilineComponent,
        ButtonComponent,
        SelectorComponent,
        BeraknatUtbyteValidationComponent,
        HotDocComponent,
        SkapaMallFranHandelssortimentComponent
    ]
})
export class AtgardVirkeskostnadComponent implements OnInit, OnDestroy {

    // Mina handelssortimentsgrupper -val
    protected handelssortimentsgrupperOptions: SelectionItem[];
    // Mina Handelssortiment -val
    protected minaHandelssortimentOptions: SelectionItem[];
    // Mina förkalkylsmallar -val
    protected forkalkylsmallarOptions: SimpleSelectionItem[];
    // Alla valbara måttslag
    protected mattslagsOptions: ISelectValue[];
    // Input till "Skapa förkalkyl"
    protected rowsForForkalkylsmallCreation: ForkalkylsmallHandelssortimentDTO[];
    // Inlästa mina förkalkylsmallar
    protected minaForkalkylsmallar: ForkalkylsmallQueryResponseDTO[];
    protected isLeveransvirke: boolean;
    protected isGrotKontrakt: boolean;
    protected kontraktReadonly: boolean;

    private readonly forkalkylmallarService = inject(ForkalkylmallarService);
    private readonly forkalkylService = inject(ForkalkylService);
    private readonly virkeskostnadMediatorService = inject(VirkeskostnaderMediatorService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService, { optional: true });
    private readonly raskModalService = inject(RaskModalService);
    private readonly virkeskostnaderUtbyteService = inject(VirkeskostnaderUtbyteService, { optional: true });
    private readonly forkalkylsmallarDialogService = inject(ForkalkylsmallarDialogService);
    private readonly minaHandelssortimentDialogService = inject(MinaHandelssortimentDialogService);
    private readonly ngZone = inject(NgZone);

    protected get hotDoc() {
        return this.binder?.hotDoc;
    }

    protected get warnings(): any[] {
        return !this.isLeveransvirke &&
            this.virkeskostnaderUtbyteService?.utbyteWarnings;
    }

    private atgard?: Atgard;
    private kontrakt: Kontrakt;
    private binder: VirkeskostnadTableBinder;
    private allaHandelssortimentsgrupper: TilldelaHandelssortimentsGruppDTO[];
    private minaHandelssortiment: IHandelssortimentVirkeskostnadOption[];
    private minaHandelssortimentDTO: ISearchHandelssortimentModel[];
    private readonly ngUnsubscribe$ = new Subject<void>();
    private hasPopulatedHandelssortiment = false;
    private virkeskostnader: Virkeskostnad[];
    private readonly defaultMattslag = Mattslag.M3F;
    private readonly canEdit = canEdit();

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.kontraktMediatorService.kontrakt$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(kontrakt => {
            this.kontrakt = kontrakt;
            this.isLeveransvirke = kontrakt.isLeveransvirke;
            this.isGrotKontrakt = kontrakt.isGrot;
            const atgard$ = this.atgardMediatorService !== null ? this.atgardMediatorService.atgard$ : of({} as Atgard);
            atgard$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(atgard => {
                this.atgard = atgard;
                this.getData();
            });
        });

        this.kontraktMediatorService.isReadonly$.pipe(
            map(readonly => readonly || !this.canEdit()),
            takeUntil(this.ngUnsubscribe$)).subscribe(readonly => this.kontraktReadonly = readonly);

        this.virkeskostnadMediatorService.virkeskostnadUpdated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (event.isMatch(this.kontrakt.guid, this.atgard?.guid) && event.source !== this) {
                    this.updatedFromEvent(event.virkeskostnad);
                }
            });

        this.virkeskostnadMediatorService.virkeskostnadCreated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (event.isMatch(this.kontrakt.guid, this.atgard?.guid) && event.source !== this) {
                    this.createdFromEvent(event.virkeskostnad);
                }
            });

        this.virkeskostnadMediatorService.virkeskostnadDeleted$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (event.isMatch(this.kontrakt.guid, this.atgard?.guid) && event.source !== this) {
                    this.deletedFromEvent(event.virkeskostnad);
                }
            });
    }

    protected get utbyteTopMargin(): string {
        if (!this.atgard?.utbyteInlastTid) {
            return "utbyte-topmargin-emptydate";
        }
        return "utbyte-topmargin-date";
    }

    protected genericSSItemFormatter = (x: SimpleSelectionItem) => x ? x.value : undefined;
    protected genericSSViewFormatter = (x: SimpleSelectionItem) => x ? x.text : undefined;

    protected refreshMinaForkalkylsmallar() {
        this.forkalkylmallarService.getMinaForkalkylsmallar().subscribe(mallar => {
            this.forkalkylsmallarOptions = this.setupMinaForkalkylsmallar(mallar, this.virkeskostnader);
        });
    }

    protected handelssortimentsgruppOptionChanged(): void {
        const diffResult = utils.diff(
            this.virkeskostnader.filter(p => !p.handelssortimentId),
            this.handelssortimentsgrupperOptions.filter(s => s.selected), s => s.handelssortimentsgruppNamn, s => s.value);

        diffResult.adds.forEach(add => {
            const hsgrupp = this.allaHandelssortimentsgrupper.find(p => p.kod === add.value);

            const option = this.mapVirkeskostnadFromHandelssortiment({
                foreslagetMattslag: this.defaultMattslag,
                handelssortimentsgruppNamn: add.value,
                handelssortimentsgruppId: hsgrupp.id,
                handelssortimentsgruppSekvensnummer: hsgrupp.sekvensnummer,
                namn: add.value,
                produkter: [],
                checked: true,
                beskrivning: add.value,
                id: null
            }, 1);

            const index = this.virkeskostnader.findIndex(p => p.handelssortimentsgruppSekvensnummer >= hsgrupp.sekvensnummer);
            this.virkeskostnader.splice(index, 0, option);
            this.virkeskostnadMediatorService.create(this.kontrakt.guid, this.atgard?.guid, option, this);
        });

        diffResult.removes.forEach(rem => {
            const kostnadRemove = this.virkeskostnader.find(p => p.handelssortimentsgruppId === rem.handelssortimentsgruppId && !p.handelssortimentId);
            if (kostnadRemove) {
                const index = this.virkeskostnader.indexOf(kostnadRemove);
                if (index > -1) {
                    this.virkeskostnader.splice(index, 1);
                }
                this.virkeskostnadMediatorService.delete(this.kontrakt.guid, this.atgard?.guid, kostnadRemove, this);
            }
        });
        this.refreshTable(true);
    }

    protected handelssortimentOptionChanged(): void {
        this.minaHandelssortimentOptions.forEach(hsg => {
            hsg.children.forEach(hs => {
                if (hs.selected) {
                    this.handelssortimentOptionChangedUpdate(hs, () => {
                        //
                    });
                } else {
                    this.handelssortimentOptionChangedRemoved(hs);
                }
            });
        });
        this.refreshTable(true);
    }

    protected handelssortimentOptionChangedUpdate(hs: SelectionItem, cb: () => void): void {
        if (!this.virkeskostnader.find(x => x.handelssortimentId === hs.value)) {
            const minHS = this.minaHandelssortiment.find(x => x.id === hs.value);
            const faktor$ = minHS.foreslagetMattslag !== Mattslag.M3F ?
                this.getMattslagKonverteringFaktorHS(minHS.foreslagetMattslag, minHS.id) :
                of(1);
            const vk = this.mapVirkeskostnadFromHandelssortiment(minHS, 1);
            this.virkeskostnader.push(vk);
            faktor$.subscribe(faktor => {
                vk.konverteringsfaktorHandelssortimentMattslag = faktor;
                this.virkeskostnadMediatorService.create(this.kontrakt.guid, this.atgard?.guid, vk, this);
                cb();
            });
        }
    }

    protected handelssortimentOptionChangedRemoved(hs: SelectionItem): void {
        const tabort = this.virkeskostnader.find(vk => vk.handelssortimentId === hs.value);
        if (tabort) {
            const hsidx = this.virkeskostnader.indexOf(tabort);
            this.virkeskostnader.splice(hsidx, 1);
            this.virkeskostnadMediatorService.delete(this.kontrakt.guid, this.atgard?.guid, tabort, this);
        }
    }

    protected forkalkylsmallSelected(forkalkylsmallId): void {
        if (this.virkeskostnader.length === 0) {
            this.applyForkalkylsMall(forkalkylsmallId);
        } else {
            firstValueFrom(this.raskModalService.confirm(new ModalSettings("Applicering av förkalkyl", "Vill du ersätta virkeskostnadsraderna med rader från vald förkalkylsmall?")))
                .then((response) => {
                    if (response === ConfirmDialogResponse.save) {
                        this.applyForkalkylsMall(forkalkylsmallId);
                    }
                });
        }
    }

    protected openRedigeraForkalkylsmallar(): void {
        this.forkalkylsmallarDialogService.openForkalkylModal().subscribe({
            next: reason => {
                this.refreshMinaForkalkylsmallar();
            },
            error: (e) => {
                this.refreshMinaForkalkylsmallar();
            }
        });
    }

    protected openMinaHandelssortiment(): void {
        this.minaHandelssortimentDialogService.openMinaHandelssortimentModal().subscribe({
            next: minaHandelssortiment => {
                this.minaHandelssortimentOptions = this.setupMinaHandelssortimentOptions(minaHandelssortiment, this.virkeskostnader, this.allaHandelssortimentsgrupper);
            },
            error: () => {
                // dismiss
            }
        });
    }

    protected clickApplyMinaHandelssortiment(): void {
        this.forkalkylmallarService.getMinaHandelssortiment(false)
            .subscribe(minaHandelssortiment => {
                this.minaHandelssortimentOptions = this.setupMinaHandelssortimentOptions(minaHandelssortiment, this.virkeskostnader, this.allaHandelssortimentsgrupper);
            });
    }

    private getData(): void {
        forkJoin([
            this.forkalkylmallarService.getMinaForkalkylsmallar(),
            this.forkalkylmallarService.getHandelssortimentGrupper(),
            this.forkalkylmallarService.getMinaHandelssortiment(false),
            this.forkalkylmallarService.getVardenByGrupp("VK_MATTSLAG")
        ])
            .subscribe(([
                minaForkalkylsmallar,
                minaHandelssortimentsgrupper,
                minaHandelssortiment,
                allaMattslag
            ]) => {
                this.virkeskostnadMediatorService.virkeskostnader$.pipe(filter(x => x !== null),
                    take(1)
                ).subscribe(event => {
                    if (ObjectUtils.isNullOrUndefined(event)) {
                        this.virkeskostnader = this.getOrCreate(new Virkeskostnader());
                    } else if (event.source !== this) {
                        this.virkeskostnader = this.getOrCreate(event.virkeskostnader);
                    }

                    this.sortVirkeskostnad();
                    this.forkalkylsmallarOptions = this.setupMinaForkalkylsmallar(minaForkalkylsmallar, this.virkeskostnader);

                    if (!this.hasPopulatedHandelssortiment) {
                        this.mattslagsOptions = allaMattslag;
                        this.handelssortimentsgrupperOptions = this.setupMinaHandelssortimentsgrupperOptions(minaHandelssortimentsgrupper, this.virkeskostnader);
                        this.minaHandelssortimentOptions = this.setupMinaHandelssortimentOptions(minaHandelssortiment, this.virkeskostnader, minaHandelssortimentsgrupper);
                        this.hasPopulatedHandelssortiment = true;
                    }
                    this.ngZone.runOutsideAngular(() => {
                        this.setupVirkeskostnaderSpreadsheet(this.virkeskostnader, this.mattslagsOptions);
                    });
                });
            });
    }

    private getOrCreate(virkeskostnader: Virkeskostnader): Virkeskostnad[] {

        if (this.isLeveransvirke) {
            return virkeskostnader.kontraktVirkeskostnader;
        } else {
            const forAtgard = virkeskostnader.atgarderVirkeskostnader
                .find(k => k.virkeskopsatgardGUID === this.atgard.guid);

            return forAtgard ? forAtgard.virkeskostnader : [];
        }
    }

    private refreshTable(hard = false): void {
        this.sortVirkeskostnad();
        this.ngZone.runOutsideAngular(() => {

            if (!hard) {
                this.binder.rebind();
            } else {
                this.binder.initTable(this.virkeskostnader, this.mattslagsOptions);
            }
        });
    }

    private updateWithFaktor(kostnad: Virkeskostnad, nyFaktor: number): void {
        kostnad.konverteringsfaktorHandelssortimentMattslag = nyFaktor;
        this.update(kostnad);
    }

    private update(kostnad: Virkeskostnad, triggerRefresh = true): void {
        this.virkeskostnadMediatorService.update(this.kontrakt.guid, this.atgard?.guid, kostnad, this);
        this.recalckostnad(kostnad);
        if (triggerRefresh) {
            this.refreshTable(false);
        }
    }

    private recalckostnad(kostnad: Virkeskostnad): void {
        kostnad.kvantitetM3fub = kostnad.konverteringsfaktorHandelssortimentMattslag * kostnad.kvantitet;
    }

    // virkeskostnad har uppdaterats
    private updatedFromEvent(updated: Virkeskostnad): void {
        const kostnad = this.virkeskostnader.find(vk => vk.guid === updated.guid);
        if (kostnad) {
            assign(kostnad, updated);
            this.recalckostnad(kostnad);
            this.refreshTable(false);
        }
    }

    // virkeskostnad har skapats
    private createdFromEvent(created: Virkeskostnad): void {
        const kostnad = clone(created);
        this.virkeskostnader.push(kostnad);
        this.recalckostnad(kostnad);
        this.refreshTable(false);
    }

    // virkeskostnad har tagits bort
    private deletedFromEvent(deleted: Virkeskostnad): void {
        const idx = this.virkeskostnader.findIndex(x => x.guid === deleted.guid);
        this.virkeskostnader.splice(idx, 1);
        this.refreshTable(false);
    }

    private sortVirkeskostnad(): void {
        this.virkeskostnader = sortBy(
            this.virkeskostnader,
            [
                p => p.handelssortimentsgruppSekvensnummer,
                p => p.handelssortimentId > 0,
                p => p.handelssortimentNamn?.toLowerCase()
            ]
        );
    }

    private newIsHSActiveForUsage(hsid: number, handelssortimentGrupper: TilldelaHandelssortimentsGruppDTO[], virkeskostnader: Virkeskostnad[]) {
        if (virkeskostnader.find(vk => vk.handelssortimentId === hsid)) {
            return true;
        }

        const realGroup = handelssortimentGrupper.find(x => x.handelssortiment.some(y => y.id === hsid));
        const realHS = realGroup?.handelssortiment.find(x => x.id === hsid);

        if (!realHS || !realHS.aktiv) {

            return false;
        }
        return true;
    }

    private isHandelssortimentActive(handelssortimentId: number, virkeskostnader: Virkeskostnad[]) {
        const activeHandelssortiment = virkeskostnader.filter(x => !isNil(x) && !isNil(x.handelssortimentId)).map(x => x.handelssortimentId);
        return activeHandelssortiment.indexOf(handelssortimentId) > -1;
    }

    private handelsSortimentsFilter(handelssortiment: ISearchHandelssortimentModel): boolean {
        const handelssortimentsgrupp = handelssortiment.handelssortimentsgrupp;
        if (!handelssortimentsgrupp) {
            return false;
        }

        switch (this.kontrakt.kontraktTyp) {
            case VirkeskopKontraktstyp.Grot:
                return handelssortimentsgrupp.kod === Handelssortimentsgrupp.Grot;

            case VirkeskopKontraktstyp.Rundvirke:
                return handelssortimentsgrupp.kod !== Handelssortimentsgrupp.Grot &&
                    handelssortimentsgrupp.kod !== Handelssortimentsgrupp.Stubbar &&
                    handelssortimentsgrupp.kod !== Handelssortimentsgrupp.Ovrigt;

            default:
                return true;
        }
    }

    private setupVirkeskostnaderSpreadsheet(virkeskostnader: Virkeskostnad[], mattslagsOptions: ISelectValue[]): void {
        virkeskostnadColumns.find(x => x.cellName === "mattslag").source = mattslagsOptions.map(o => o.kod);
        this.binder = new VirkeskostnadTableBinder(virkeskostnadColumns, this.ngZone);
        this.binder.modelChange$.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(change => {
            this.update(change, false);

            // Hantera varningar
            this.virkeskostnaderUtbyteService?.checkEjHanteratUtbyte(this.virkeskostnader);
        });
        this.binder.mattslagChange$.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(change => {
            if (change.handelssortimentId) {
                this.getMattslagKonverteringFaktorHS(change.mattslag, change.handelssortimentId)
                    .subscribe(faktor => this.updateWithFaktor(change, faktor));
            } else if (change.handelssortimentsgruppId) {
                this.getMattslagKonverteringFaktorHSG(change.mattslag, change.handelssortimentsgruppId)
                    .subscribe(faktor => this.updateWithFaktor(change, faktor));
            }
        });

        this.virkeskostnader = virkeskostnader;
        this.sortVirkeskostnad();
        this.refreshTable(true);
    }

    private setupMinaHandelssortimentsgrupperOptions(
        minaHandelssortimentsgrupper: TilldelaHandelssortimentsGruppDTO[],
        virkeskostnader: Virkeskostnad[]): SelectionItem[] {

        this.allaHandelssortimentsgrupper = minaHandelssortimentsgrupper;

        const hsgSelectionItems = orderBy(minaHandelssortimentsgrupper.filter(y => y.defaultHandelssortimentId > 0), p => p.sekvensnummer)
            .map(p => {
                const item = new SelectionItem();
                item.expanded = false;
                item.value = p.kod;
                item.text = p.namn;
                item.selected = false;
                item.id = p.id;
                return item;
            });

        hsgSelectionItems.forEach(hsg => {
            hsg.selected = virkeskostnader
                .some(s => s.handelssortimentsgruppNamn === hsg.value && !s.handelssortimentId);
        });
        return hsgSelectionItems;
    }

    private setupMinaHandelssortimentOptions(
        minaHandelssortiment: ISearchHandelssortimentModel[],
        virkeskostnader: Virkeskostnad[],
        minaHandelssortimentsgrupper: TilldelaHandelssortimentsGruppDTO[]): SelectionItem[] {

        this.minaHandelssortimentDTO = minaHandelssortiment;

        const filteredMinaHandelssortiment = minaHandelssortiment.filter(hs => this.handelsSortimentsFilter(hs));
        const handelssortimentVirkeskostnadOptions = filteredMinaHandelssortiment.map(hs => (
            new HandelssortimentVirkeskostnadOption(
                hs.id,
                this.isHandelssortimentActive(hs.id, this.virkeskostnader),
                hs.namn,
                hs.namn,
                hs.handelssortimentsgrupp.namn,
                hs.handelssortimentsgrupp.sekvensnummer,
                hs.handelssortimentsgrupp.id,
                hs.produkter ? hs.produkter.map(p => p.namn) : [],
                hs.aktiv,
                hs.foreslagetMattslag)
        ));

        virkeskostnader.forEach(vk => {
            if (filteredMinaHandelssortiment.filter(hsvk => hsvk.id === vk.handelssortimentId).length === 0) {
                handelssortimentVirkeskostnadOptions.push(new HandelssortimentVirkeskostnadOption(
                    vk.handelssortimentId,
                    true,
                    vk.handelssortimentNamn,
                    vk.handelssortimentNamn,
                    vk.handelssortimentsgruppNamn,
                    vk.handelssortimentsgruppSekvensnummer,
                    vk.handelssortimentsgruppId,
                    [],
                    true,
                    vk.mattslag
                ));
            }
        });

        const minaHandelssortimentOptions = handelssortimentVirkeskostnadOptions
            .filter(o => o.id)
            .sort((a, b) => a.beskrivning < b.beskrivning ? -1 : 1);
        this.minaHandelssortiment = minaHandelssortimentOptions;

        const dict = minaHandelssortimentOptions
            .reduce((kv, obj) => { const group = obj.handelssortimentsgruppNamn; kv[group] = (kv[group] || []).concat(obj); return kv; }, {});
        const options = Object.keys(dict).map(k => dict[k]) as IHandelssortimentVirkeskostnadOption[][];

        return options.map(o => {
            const group = {
                id: o[0].handelssortimentsgruppSekvensnummer,
                text: o[0].handelssortimentsgruppNamn,
                value: o[0].handelssortimentsgruppNamn,
                children: o.filter(opt => this.newIsHSActiveForUsage(opt.id, minaHandelssortimentsgrupper, virkeskostnader)).map(opt => ({
                    text: opt.namn,
                    value: opt.id,
                    selected: opt.checked
                }))
            } as SelectionItem;
            group.partialSelected = group.children.some(opt => opt.selected) && !group.children.every(opt => opt.selected);
            group.selected = group.children.every(opt => opt.selected);
            return group;
        }).sort((a, b) => a.id > b.id ? 1 : -1);
    }

    private setupMinaForkalkylsmallar(minaForkalkylsmallar: ForkalkylsmallQueryResponseDTO[], virkeskostnader: Virkeskostnad[]): SimpleSelectionItem[] {
        this.minaForkalkylsmallar = minaForkalkylsmallar;

        this.rowsForForkalkylsmallCreation = virkeskostnader.filter(kostnad => (!!kostnad.handelssortimentId || !!kostnad.handelssortimentsgruppId))
            .map(kostnad => ({
                handelssortimentId: kostnad.handelssortimentId,
                handelssortimentsGruppId: kostnad.handelssortimentsgruppId,
                grundpris: kostnad.grundpris,
                tillaggAvdrag: kostnad.tillaggAvdrag,
                tackningsbidrag: kostnad.tackningsbidrag,
                mattslag: kostnad.mattslag,
                fsc: kostnad.fsc,
                pefc: kostnad.pefcSC
            } as ForkalkylsmallHandelssortimentDTO));

        return minaForkalkylsmallar.map(mall => ({
            text: mall.namn,
            value: mall.id
        }));
    }

    private applyForkalkylsMall(mallId: number): void {
        const observables: Observable<any>[] = [];
        const selectedMall = this.minaForkalkylsmallar.find(x => x.id === mallId);
        const oldVirkeskostnader = this.virkeskostnader;
        const createVirkeskonstad = (mall, hs, grupp, faktor) => {
            // För att remove/add så fiskar vi upp existerande objekt i de fall dom redan finns.
            let virkeskostnad = oldVirkeskostnader.find(x => (x.handelssortimentId === hs?.id && hs?.id) ||
                (x.handelssortimentsgruppId === grupp?.id && !x.handelssortimentId && grupp?.id && !hs?.id));
            if (!virkeskostnad) {
                virkeskostnad = new Virkeskostnad();
                virkeskostnad.guid = GuidUtils.newGuid();
            }

            const vk = this.mapVirkeskostnadFromForkalkylsmall(virkeskostnad, mall, hs, grupp, faktor);
            this.virkeskostnader.push(vk);
        };

        if (selectedMall !== undefined) {
            this.virkeskostnader = [];
            selectedMall.handelssortiment.forEach(mallSortiment => {
                // Med tanke på att forkalkylen inte levererar gruppen så måste vi gå genom handelssortimentslistan för att kunna hitta gruppen
                let realGroup = this.allaHandelssortimentsgrupper.find(x => x.handelssortiment.some(y => y.id === mallSortiment.handelssortimentId));
                if (!realGroup) {
                    realGroup = this.allaHandelssortimentsgrupper.find(x => x.id === mallSortiment.handelssortimentsGruppId);
                }
                const realHS = realGroup.handelssortiment.find(x => x.id === mallSortiment.handelssortimentId);
                if (mallSortiment.handelssortimentId && realHS && realHS.aktiv) {
                    observables.push(this.getMattslagKonverteringFaktorHS(mallSortiment.mattslag, mallSortiment.handelssortimentId).pipe(map((faktor) => {
                        createVirkeskonstad(mallSortiment, realHS, realGroup, faktor);
                    })));
                } else if (mallSortiment.handelssortimentsGruppId && !mallSortiment.handelssortimentId && realGroup) {
                    observables.push(this.getMattslagKonverteringFaktorHSG(mallSortiment.mattslag, mallSortiment.handelssortimentsGruppId).pipe(map((faktor) => {
                        createVirkeskonstad(mallSortiment, realHS, realGroup, faktor);
                    })));
                }
            });
        }

        forkJoin(observables).subscribe((finished) => {
            this.minaHandelssortimentOptions = this.setupMinaHandelssortimentOptions(this.minaHandelssortimentDTO, this.virkeskostnader, this.allaHandelssortimentsgrupper);
            this.handelssortimentsgrupperOptions = this.setupMinaHandelssortimentsgrupperOptions(this.allaHandelssortimentsgrupper, this.virkeskostnader);
            const diffResult = utils.diff(oldVirkeskostnader, this.virkeskostnader, a => a.guid, b => b.guid);

            diffResult.adds.forEach(vk => {
                this.virkeskostnadMediatorService.create(this.kontrakt.guid, this.atgard?.guid, vk, this);
                this.virkeskostnadMediatorService.update(this.kontrakt.guid, this.atgard?.guid, vk, this);
            });

            diffResult.matched.forEach(vk => {
                this.virkeskostnadMediatorService.update(this.kontrakt.guid, this.atgard?.guid, vk.other, this);
            });

            diffResult.removes.forEach(vk => {
                this.virkeskostnadMediatorService.delete(this.kontrakt.guid, this.atgard?.guid, vk, this);
            });

            this.refreshTable(true);
        });
    }

    private mapVirkeskostnadFromHandelssortiment(hs: IHandelssortimentVirkeskostnadOption, faktor: number): Virkeskostnad {
        const virkeskostnad = new Virkeskostnad();
        virkeskostnad.guid = GuidUtils.newGuid();
        virkeskostnad.handelssortimentId = hs.id;
        virkeskostnad.handelssortimentNamn = hs.namn;
        virkeskostnad.handelssortimentsgruppNamn = hs.handelssortimentsgruppNamn;
        virkeskostnad.handelssortimentsgruppId = hs.handelssortimentsgruppId;
        virkeskostnad.handelssortimentsgruppSekvensnummer = hs.handelssortimentsgruppSekvensnummer;
        virkeskostnad.grundpris = 0;
        virkeskostnad.mattslag = hs.foreslagetMattslag;
        virkeskostnad.konverteringsfaktorHandelssortimentMattslag = faktor;
        virkeskostnad.fsc = 0;
        virkeskostnad.kvantitet = 0;
        virkeskostnad.pefcSC = 0;
        virkeskostnad.tackningsbidrag = 0;
        virkeskostnad.tillaggAvdrag = 0;
        return virkeskostnad;
    }

    private mapVirkeskostnadFromForkalkylsmall(
        virkeskostnad: Virkeskostnad,
        mallSortiment: ForkalkylsmallHandelssortimentDTO,
        hs: TilldelaHandelssortimentDTO,
        hsg: TilldelaHandelssortimentsGruppDTO,
        faktor: number): Virkeskostnad {
        virkeskostnad.handelssortimentId = hs?.id;
        virkeskostnad.handelssortimentNamn = hs?.namn;
        virkeskostnad.handelssortimentsgruppNamn = hsg.namn;
        virkeskostnad.handelssortimentsgruppId = hsg.id;
        virkeskostnad.handelssortimentsgruppSekvensnummer = hsg.sekvensnummer;
        virkeskostnad.grundpris = mallSortiment.grundpris;
        virkeskostnad.mattslag = mallSortiment.mattslag;
        virkeskostnad.konverteringsfaktorHandelssortimentMattslag = faktor;
        virkeskostnad.fsc = mallSortiment.fsc;
        virkeskostnad.kvantitet = 0;
        virkeskostnad.pefcSC = mallSortiment.pefc;
        virkeskostnad.tackningsbidrag = mallSortiment.tackningsbidrag;
        virkeskostnad.tillaggAvdrag = mallSortiment.tillaggAvdrag;
        virkeskostnad.id = mallSortiment.id;
        return virkeskostnad;
    }

    private getMattslagKonverteringFaktorHSG(mattslag: string, handelssortimentsGruppId: number = null): Observable<number> {
        // Produktionsområde är tills vidare hårdkodat till Karlskoga
        // Detta måste ändras innan VK är lanserat.
        // Vi har just nu inget produktionsområde att gå på (Åtgäder kan saknas etc), därav hårdkodning.

        return this.forkalkylService.getAverageMattslag(
            {
                produktionsomradeId: StatiskaProduktionsomradeIdn.bergslagenVast,
                mattslag: mattslag,
                handelssortimentsGruppId: handelssortimentsGruppId
            }
        );
    }

    private getMattslagKonverteringFaktorHS(mattslag: string, handelssortimentId: number = null): Observable<number> {
        // Produktionsområde är tills vidare hårdkodat till Karlskoga
        // Detta måste ändras innan VK är lanserat.
        // Vi har just nu inget produktionsområde att gå på (Åtgäder kan saknas etc), därav hårdkodning.

        return this.forkalkylService.getAverageMattslag(
            {
                produktionsomradeId: StatiskaProduktionsomradeIdn.bergslagenVast,
                mattslag: mattslag,
                handelssortimentId: handelssortimentId
            }
        );
    }
}
